import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PawPrint } from "lucide-react";

export default function SignIn() {
  const { signIn, error, setError } = useAuth();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    const ok = await signIn(email, name);
    setSubmitting(false);
    if (ok) navigate("/dashboard");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1482066490729-6f26115b60dc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxjYXQlMjBzbGVlcGluZyUyMGNvenl8ZW58MHx8fHwxNzgzMjgwMjY3fDA&ixlib=rb-4.1.0&q=85"
          alt="Cozy cat"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1a2f24]/40 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="flex items-center gap-2" data-testid="brand-back-link">
            <span className="w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              <PawPrint size={18} />
            </span>
            <span className="font-display text-lg font-semibold">Cat Kidney Compass</span>
          </Link>
          <p className="font-display text-3xl leading-snug max-w-md drop-shadow">
            Kidney disease is a long road. You don&apos;t have to walk it alone.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <form onSubmit={onSubmit} className="w-full max-w-md" data-testid="signin-form">
          <h1 className="font-display text-3xl">Sign in</h1>
          <p className="text-muted-foreground mt-2 text-sm">
            Enter your name and email — we&apos;ll pick up where you left off, or start you a new
            account.
          </p>

          <div className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input
                id="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="signin-name-input"
                className="mt-2 rounded-xl"
                placeholder="Alex"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="signin-email-input"
                className="mt-2 rounded-xl"
                placeholder="you@example.com"
              />
            </div>
          </div>

          {error && (
            <p className="mt-4 text-sm text-destructive" data-testid="signin-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting}
            data-testid="signin-submit-button"
            className="mt-6 w-full rounded-full h-11"
          >
            {submitting ? "Signing in…" : "Continue"}
          </Button>

          <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
            No password needed — we use your email to remember your cats and lab history.
          </p>
        </form>
      </div>
    </div>
  );
}
